import React, { useMemo } from 'react';
import { Avatar, Box, Card, CardActionArea, Chip, Stack, Switch, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

export type EvaluationSummary = {
  protocolCount: number;
  elementCount: number;
  protocolNames: string[];
};

type EvaluationTypeCardToggle = {
  /** Form field name (in the surrounding FormProvider) that stores the enabled boolean. */
  name: FieldPath<FieldValues>;
  ariaLabel: string;
};

type EvaluationTypeCardProps = {
  title: string;
  description: string;
  summary: EvaluationSummary;
  icon: React.ReactNode;
  accentColor: string;
  emptyMessage: string;
  protocolsLabel: string;
  elementsLabel: string;
  includedLabel: string;
  moreLabel: string;
  ariaLabel: string;
  onClick: () => void;
  /** When provided, the card renders a Switch in the top-right bound to this form field. */
  toggle?: EvaluationTypeCardToggle;
  /** Message shown in the body when the toggle is off. */
  disabledMessage?: string;
};

const EvaluationTypeCard: React.FC<EvaluationTypeCardProps> = ({
  title,
  description,
  summary,
  icon,
  accentColor,
  emptyMessage,
  protocolsLabel,
  elementsLabel,
  includedLabel,
  moreLabel,
  ariaLabel,
  onClick,
  toggle,
  disabledMessage
}) => {
  const { protocolCount, elementCount, protocolNames } = summary;
  const isEmpty = protocolCount === 0 && elementCount === 0;

  const previewText = useMemo(() => {
    if (protocolNames.length === 0) {
      return '';
    }
    const preview = protocolNames.slice(0, 3);
    const remaining = protocolNames.length - preview.length;
    if (remaining <= 0) {
      return preview.join(', ');
    }
    return `${preview.join(', ')} +${remaining} ${moreLabel}`;
  }, [protocolNames, moreLabel]);

  const renderCard = (isDisabled: boolean, toggleSlot: React.ReactNode) => (
    <Card
      sx={{
        height: '100%',
        minHeight: 160,
        position: 'relative',
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: 'none',
        transition: 'border-color 0.15s',
        '&:hover': {
          borderColor: isDisabled ? 'grey.200' : 'grey.300'
        }
      }}
    >
      {toggleSlot}

      <CardActionArea
        onClick={onClick}
        disabled={isDisabled}
        aria-label={ariaLabel}
        sx={{
          height: '100%',
          p: 2.5,
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          '&.Mui-disabled': { opacity: 0.5 }
        }}
      >
        <Box display="flex" width="100%" mb={2} sx={{ pr: toggle ? 5 : 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              mr: 1.5,
              bgcolor: alpha(accentColor, isEmpty || isDisabled ? 0.25 : 0.9),
              color: isEmpty || isDisabled ? 'text.secondary' : 'common.white',
              '& .MuiSvgIcon-root': { fontSize: 22 }
            }}
          >
            {icon}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }} noWrap>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {description}
            </Typography>
          </Box>
        </Box>

        <Box width="100%" flexGrow={1}>
          {isDisabled ? (
            <Typography variant="body2" color="text.secondary">
              {disabledMessage}
            </Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap">
                <Chip
                  label={`${protocolCount} ${protocolsLabel}`}
                  size="small"
                  color={protocolCount > 0 ? 'primary' : 'default'}
                  variant={protocolCount > 0 ? 'filled' : 'outlined'}
                />
                <Chip label={`${elementCount} ${elementsLabel}`} size="small" variant="outlined" />
              </Stack>

              {isEmpty ? (
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {includedLabel}: {previewText}
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );

  if (!toggle) {
    return renderCard(false, null);
  }

  return (
    <Controller
      name={toggle.name}
      render={({ field }) =>
        renderCard(
          !field.value,
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }} onClick={(e) => e.stopPropagation()}>
            <Switch
              size="small"
              checked={!!field.value}
              onChange={(_, next) => field.onChange(next)}
              slotProps={{ input: { 'aria-label': toggle.ariaLabel } }}
            />
          </Box>
        )
      }
    />
  );
};

export default EvaluationTypeCard;
